<script>
import conditionalGroupReset from '@/mixins/conditionalGroupReset';
import { BIconInfoCircle, BTooltip, BFormSelect } from 'bootstrap-vue';
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import agreementValidation from '@/mixins/agreementValidation'
import { mapState } from 'vuex';

export default {
	mixins: [conditionalGroupReset,validationMixin,agreementValidation],
	components:{ BIconInfoCircle, BTooltip, BFormSelect },
	props: {
		label: String,
		value: Boolean,
		options: {
			type: Array,
			default() {
				return [
					{ value: true, text: 'Yes' },
					{ value: false, text: 'No' },
				];
			},
		},
		cols: {
			type: String,
			default() {
				return '2';
			},
		},
		testid:String,
		onChange: Function,
		disabled:Boolean,
		toolTipText:String,
		isValidationOn:Boolean,
		displayAsDropdown: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			localValue: null,
			showToolTip:true
		};
	},
	watch: {
		localValue() {
			this.$emit('input', this.localValue);
		},
		value() {
			this.updateValue();
		},
	},
	mounted() {
		this.updateValue();
	},
	computed:{
		...mapState(['currentRoleLevel']),
		isUserSeparatingParty(){
			return this.currentRoleLevel.roleAssociated === 'party'
		},
		yesOption() {
			return this.options?.[0] || { value: true, text: 'Yes' };
		},
		noOption() {
			return this.options?.[1] || { value: false, text: 'No' };
		},
		isYesSelected() {
			const val = this.$v.localValue.$model;
			if (val === this.yesOption.value) return true;
			if (val === this.noOption.value || val === null || val === undefined) return false;
			// Follow-up answers (e.g. 'equally') still mean Yes was selected
			return true;
		},
	},
	methods: {
		updateValue() {
			const next =
				this.value === null || this.value === undefined
					? this.noOption.value
					: this.value;
			if (this.localValue !== next) {
				this.localValue = next;
			}
		},
		onChangeByUser(event){
			this.$emit('changeValue', event);
		},
		validateState(){
			const { $dirty, $error } = this.$v.localValue;
			return $dirty ? !$error : null;
		},
		triggerValidation(){
			this.$v.$touch()
		},
		setValueByUser(value){
			if (this.disabled) return;
			this.$v.localValue.$model = value;
			this.onChangeByUser(value);
		}
	},
	validations:{
		localValue:{
			required
		}
	}
};
</script>

<template>
	<b-form-group :label="label" :class="{'field-required-group': isValidationOn}">
		<b-row no-gutters>
			<b-col :class="'col-6 col-sm-'+cols">
				<b-form-select
					v-if="displayAsDropdown"
					v-model="$v.localValue.$model"
					:options="options"
					:on-change="onChange"
					:data-testid="testid"
					@change="onChangeByUser"
					:disabled="disabled"
					:state="isValidationOn? validateState(): null"
					:class="{ 'custom-disabled': disabled }"
				/>
				<div
					v-else
					class="yesno-toggle"
					:class="{ 'is-disabled': disabled }"
					role="group"
					:aria-label="label"
				>
					<div class="yesno-toggle__thumb" :class="{ 'is-yes': isYesSelected }" />
					<button
						type="button"
						class="yesno-toggle__btn"
						:class="{ 'is-active': isYesSelected }"
						@click="setValueByUser(yesOption.value)"
						:disabled="disabled"
						:data-testid="`${testid || 'yes-no-choice'}-yes`"
					>
						{{ yesOption.text }}
					</button>
					<button
						type="button"
						class="yesno-toggle__btn"
						:class="{ 'is-active': !isYesSelected }"
						@click="setValueByUser(noOption.value)"
						:disabled="disabled"
						:data-testid="`${testid || 'yes-no-choice'}-no`"
					>
						{{ noOption.text }}
					</button>
				</div>
				<div
					v-if="$v.localValue.$error && isValidationOn"
					class="invalid-feedback d-block col-12"
				>
					This is a required field.
				</div>
			</b-col>
			<b-col :cols="cols" class="info-outer" v-if="toolTipText">
				<b-icon-info-circle class="ml-2"
					v-if="toolTipText && isUserSeparatingParty"
					v-b-tooltip.html="{ title: toolTipText, boundary: 'viewport' }"
					>
				</b-icon-info-circle>
			</b-col>
		</b-row>
	</b-form-group>
</template>

<style scoped>
.info-outer {
	align-self: center;
    margin-left: 0.5rem;
}

.yesno-toggle {
	--yesno-toggle-max-width: 260px;
	--yesno-toggle-pad: 4px;
	position: relative;
	display: grid;
	grid-template-columns: 1fr 1fr;
	align-items: center;
	width: 100%;
	max-width: var(--yesno-toggle-max-width);
	background: var(--yesno-toggle-track-bg, rgba(0, 0, 0, 0.06));
	border-radius: 999px;
	padding: var(--yesno-toggle-pad);
	gap: 0;
	user-select: none;
}

.yesno-toggle__thumb {
	position: absolute;
	top: var(--yesno-toggle-pad);
	left: var(--yesno-toggle-pad);
	width: calc(50% - var(--yesno-toggle-pad));
	height: calc(100% - (var(--yesno-toggle-pad) * 2));
	border-radius: 999px;
	background: var(--yesno-toggle-accent, var(--primary, #007bff));
	transition: transform 160ms ease;
}

.yesno-toggle__thumb.is-yes {
	transform: translateX(0);
}

.yesno-toggle__thumb:not(.is-yes) {
	transform: translateX(100%);
}

.yesno-toggle__btn {
	position: relative;
	z-index: 1;
	border: 0;
	background: transparent;
	padding: 6px 10px;
	border-radius: 999px;
	font: inherit;
	font-size: 0.875rem;
	font-weight: 600;
	line-height: 1;
	color: var(--yesno-toggle-text, var(--secondary, #6c757d));
	outline: none;
}

.yesno-toggle__btn.is-active {
	color: var(--yesno-toggle-active-text, var(--white, #ffffff));
}

.yesno-toggle.is-disabled {
	opacity: 0.6;
}

.yesno-toggle.is-disabled .yesno-toggle__btn {
	cursor: not-allowed;
}
</style>
